import { useCallback, useEffect, useRef, useState } from 'react';
import type { EFormSchema } from './schema';

const MAX_HISTORY = 50;
const DEBOUNCE_MS = 400;

function cloneSchema(schema: EFormSchema): EFormSchema {
  return JSON.parse(JSON.stringify(schema)) as EFormSchema;
}

function sameSchema(a: EFormSchema, b: EFormSchema): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Schema state with undo/redo. Rapid edits (typing in the inspector) collapse
 * into one history entry after a short quiet period.
 */
export function useSchemaHistory(initial: EFormSchema) {
  const [schema, setSchemaState] = useState<EFormSchema>(initial);
  const [revision, setRevision] = useState(0);
  const pastRef = useRef<EFormSchema[]>([]);
  const futureRef = useRef<EFormSchema[]>([]);
  const checkpointRef = useRef<EFormSchema | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const bump = useCallback(() => setRevision((n) => n + 1), []);

  const clearDebounce = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  useEffect(() => () => clearDebounce(), [clearDebounce]);

  const flushCheckpoint = useCallback(() => {
    clearDebounce();
    if (!checkpointRef.current) return;
    pastRef.current = [...pastRef.current, checkpointRef.current].slice(-MAX_HISTORY);
    checkpointRef.current = null;
    futureRef.current = [];
    bump();
  }, [bump, clearDebounce]);

  const setSchema = useCallback(
    (next: EFormSchema | ((prev: EFormSchema) => EFormSchema)) => {
      let changed = false;
      setSchemaState((prev) => {
        const resolved = typeof next === 'function' ? next(prev) : next;
        if (sameSchema(prev, resolved)) return prev;

        if (!checkpointRef.current) {
          checkpointRef.current = cloneSchema(prev);
        }
        futureRef.current = [];
        changed = true;
        return resolved;
      });

      if (!changed) return;

      clearDebounce();
      debounceRef.current = setTimeout(() => {
        if (checkpointRef.current) {
          pastRef.current = [...pastRef.current, checkpointRef.current].slice(-MAX_HISTORY);
          checkpointRef.current = null;
          futureRef.current = [];
          bump();
        }
        debounceRef.current = null;
      }, DEBOUNCE_MS);
      bump();
    },
    [bump, clearDebounce],
  );

  const replaceSchema = useCallback(
    (next: EFormSchema, opts?: { recordHistory?: boolean }) => {
      clearDebounce();
      checkpointRef.current = null;
      setSchemaState((prev) => {
        if (opts?.recordHistory && !sameSchema(prev, next)) {
          pastRef.current = [...pastRef.current, cloneSchema(prev)].slice(-MAX_HISTORY);
          futureRef.current = [];
        } else if (!opts?.recordHistory) {
          pastRef.current = [];
          futureRef.current = [];
        }
        return next;
      });
      bump();
    },
    [bump, clearDebounce],
  );

  /** Update present schema without touching undo history (e.g. live drag previews). */
  const setSchemaQuiet = useCallback(
    (next: EFormSchema | ((prev: EFormSchema) => EFormSchema)) => {
      clearDebounce();
      checkpointRef.current = null;
      setSchemaState((prev) => {
        const resolved = typeof next === 'function' ? next(prev) : next;
        if (sameSchema(prev, resolved)) return prev;
        return resolved;
      });
    },
    [clearDebounce],
  );

  /** Record one undo step from a snapshot taken before a multi-update gesture (e.g. drag). */
  const commitHistoryFrom = useCallback(
    (before: EFormSchema) => {
      clearDebounce();
      checkpointRef.current = null;
      let changed = false;
      setSchemaState((prev) => {
        if (sameSchema(before, prev)) return prev;
        changed = true;
        pastRef.current = [...pastRef.current, cloneSchema(before)].slice(-MAX_HISTORY);
        futureRef.current = [];
        return prev;
      });
      if (changed) bump();
    },
    [bump, clearDebounce],
  );

  const undo = useCallback(() => {
    let next: EFormSchema | null = null;
    setSchemaState((prev) => {
      if (checkpointRef.current) {
        clearDebounce();
        next = checkpointRef.current;
        checkpointRef.current = null;
        futureRef.current = [...futureRef.current, cloneSchema(prev)];
        return next;
      }
      if (!pastRef.current.length) return prev;
      next = pastRef.current[pastRef.current.length - 1];
      pastRef.current = pastRef.current.slice(0, -1);
      futureRef.current = [...futureRef.current, cloneSchema(prev)];
      return next;
    });
    if (next) bump();
  }, [bump, clearDebounce]);

  const redo = useCallback(() => {
    flushCheckpoint();
    let next: EFormSchema | null = null;
    setSchemaState((prev) => {
      if (!futureRef.current.length) return prev;
      next = futureRef.current[futureRef.current.length - 1];
      futureRef.current = futureRef.current.slice(0, -1);
      pastRef.current = [...pastRef.current, cloneSchema(prev)].slice(-MAX_HISTORY);
      return next;
    });
    if (next) bump();
  }, [bump, flushCheckpoint]);

  const canUndo = pastRef.current.length > 0 || checkpointRef.current != null;
  const canRedo = futureRef.current.length > 0;
  void revision;

  return {
    schema,
    setSchema,
    setSchemaQuiet,
    commitHistoryFrom,
    replaceSchema,
    undo,
    redo,
    canUndo,
    canRedo,
  };
}

export function isEditableKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return Boolean(target.closest('[contenteditable="true"]'));
}
